import { useDashboardCache } from './dashboardCache.js';
import { useApi } from '../composables/useApi.js';
import { useAuthStore } from '../stores/auth.js';

/**
 * Dashboard API Service with intelligent caching
 * 
 * This service provides cached dashboard statistics while maintaining
 * real-time data freshness through intelligent invalidation.
 */
class DashboardApiService {
  constructor() {
    this.cache = useDashboardCache();
    this.api = useApi();
    this.initialized = false;
    this.initialize();
  }

  initialize() {
    if (!this.initialized) {
      console.log('[DashboardAPI] Initializing dashboard API with intelligent caching');
      this.cache.initialize();
      this.initialized = true;
    }
  }

  /**
   * Get dashboard statistics with caching
   */
  async getDashboardStats(filters = {}) {
    const cacheKey = 'dashboard_stats';
    
    return await this.cache.get(
      cacheKey,
      () => this.fetchDashboardStats(filters),
      {
        ttl: 5 * 60 * 1000, // 5 minutes
        type: 'dashboard_stats',
        staleWhileRevalidate: true,
        backgroundRefresh: true
      }
    );
  }

  /**
   * Get payment statistics from dashboard data
   */
  async getPaymentStats(dateRange = {}) {
    const cacheKey = 'payment_stats';
    
    return await this.cache.get(
      cacheKey,
      () => this.fetchPaymentStats(dateRange),
      {
        ttl: 3 * 60 * 1000, // 3 minutes
        type: 'payment_stats',
        staleWhileRevalidate: true,
        backgroundRefresh: true
      }
    );
  }

  /**
   * Get order statistics from dashboard data
   */
  async getOrderStats(dateRange = {}) {
    const cacheKey = 'order_stats';
    
    return await this.cache.get(
      cacheKey,
      () => this.fetchOrderStats(dateRange),
      {
        ttl: 3 * 60 * 1000, // 3 minutes
        type: 'order_stats',
        staleWhileRevalidate: true,
        backgroundRefresh: true
      }
    );
  }

  /**
   * Get quick stats for dashboard widgets
   */
  async getQuickStats() {
    const cacheKey = 'quick_stats';
    
    return await this.cache.get(
      cacheKey,
      () => this.fetchQuickStats(),
      {
        ttl: 2 * 60 * 1000, // 2 minutes
        type: 'quick_stats',
        staleWhileRevalidate: true,
        backgroundRefresh: true
      }
    );
  }

  /**
   * Get chart data with caching
   */
  async getChartData(chartType, period = 'month') {
    const cacheKey = `chart_${chartType}_${period}`;
    
    return await this.cache.get(
      cacheKey,
      () => this.fetchChartData(chartType, period),
      {
        ttl: 10 * 60 * 1000, // 10 minutes
        type: 'chart_data',
        staleWhileRevalidate: true,
        backgroundRefresh: true
      }
    );
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cache.getStats();
  }

  // Private methods for fetching data

  /**
   * Fetch dashboard statistics from API
   */
  async fetchDashboardStats(filters = {}) {
    console.log('[DashboardAPI] Fetching dashboard stats from API');
    
    try {
      const response = await this.api.get('/dashboard/stats', { params: filters });
      console.log('[DashboardAPI] Dashboard stats fetched successfully');
      return response.data;
    } catch (error) {
      console.error('[DashboardAPI] Error fetching dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Fetch payment statistics from dashboard data
   */
  async fetchPaymentStats(dateRange = {}) {
    console.log('[DashboardAPI] Fetching payment stats from dashboard data');
    
    try {
      // Get the full dashboard stats and extract payment data
      const dashboardStats = await this.fetchDashboardStats(dateRange);
      
      const paymentStats = {
        total_sales: dashboardStats.total_sales || 0,
        year_sales: dashboardStats.year_sales || 0,
        month_sales: dashboardStats.month_sales || 0,
        week_sales: dashboardStats.week_sales || 0,
        today_sales: dashboardStats.today_sales || 0,
        // Add growth calculations
        sales_growth: {
          month_vs_year: dashboardStats.year_sales > 0 ? 
            ((dashboardStats.month_sales / dashboardStats.year_sales) * 100).toFixed(2) : 0,
          week_vs_month: dashboardStats.month_sales > 0 ? 
            ((dashboardStats.week_sales / dashboardStats.month_sales) * 100).toFixed(2) : 0,
          today_vs_week: dashboardStats.week_sales > 0 ? 
            ((dashboardStats.today_sales / dashboardStats.week_sales) * 100).toFixed(2) : 0
        }
      };
      
      console.log('[DashboardAPI] Payment stats processed successfully');
      return paymentStats;
    } catch (error) {
      console.error('[DashboardAPI] Error fetching payment stats:', error);
      throw error;
    }
  }

  /**
   * Fetch order statistics from dashboard data
   */
  async fetchOrderStats(dateRange = {}) {
    console.log('[DashboardAPI] Fetching order stats from dashboard data');
    
    try {
      // Get the full dashboard stats and extract order data
      const dashboardStats = await this.fetchDashboardStats(dateRange);
      
      const orderStats = {
        total_orders: dashboardStats.total_orders || 0,
        year_orders: dashboardStats.year_orders || 0,
        month_orders: dashboardStats.month_orders || 0,
        week_orders: dashboardStats.week_orders || 0,
        today_orders: dashboardStats.today_orders || 0,
        // Add growth calculations
        order_growth: {
          month_vs_year: dashboardStats.year_orders > 0 ? 
            ((dashboardStats.month_orders / dashboardStats.year_orders) * 100).toFixed(2) : 0,
          week_vs_month: dashboardStats.month_orders > 0 ? 
            ((dashboardStats.week_orders / dashboardStats.month_orders) * 100).toFixed(2) : 0,
          today_vs_week: dashboardStats.week_orders > 0 ? 
            ((dashboardStats.today_orders / dashboardStats.week_orders) * 100).toFixed(2) : 0
        }
      };
      
      console.log('[DashboardAPI] Order stats processed successfully');
      return orderStats;
    } catch (error) {
      console.error('[DashboardAPI] Error fetching order stats:', error);
      throw error;
    }
  }

  /**
   * Fetch quick statistics from dashboard data
   */
  async fetchQuickStats() {
    console.log('[DashboardAPI] Fetching quick stats from dashboard data');
    
    try {
      // Get the full dashboard stats and extract key metrics
      const dashboardStats = await this.fetchDashboardStats();
      
      const quickStats = {
        today_orders: dashboardStats.today_orders || 0,
        today_sales: dashboardStats.today_sales || 0,
        today_customers: dashboardStats.today_customers || 0,
        today_expenses: dashboardStats.today_expenses || 0,
        // Week totals
        week_orders: dashboardStats.week_orders || 0,
        week_sales: dashboardStats.week_sales || 0,
        week_customers: dashboardStats.week_customers || 0,
        week_expenses: dashboardStats.week_expenses || 0,
        // Month totals
        month_orders: dashboardStats.month_orders || 0,
        month_sales: dashboardStats.month_sales || 0,
        month_customers: dashboardStats.month_customers || 0,
        month_expenses: dashboardStats.month_expenses || 0,
        // Quick calculations
        profit_margin: dashboardStats.month_sales > 0 ? 
          (((dashboardStats.month_sales - dashboardStats.month_expenses) / dashboardStats.month_sales) * 100).toFixed(2) : 0,
        avg_order_value: dashboardStats.month_orders > 0 ? 
          (dashboardStats.month_sales / dashboardStats.month_orders).toFixed(2) : 0
      };
      
      console.log('[DashboardAPI] Quick stats processed successfully');
      return quickStats;
    } catch (error) {
      console.error('[DashboardAPI] Error fetching quick stats:', error);
      throw error;
    }
  }

  /**
   * Fetch chart data from existing API endpoints
   */
  async fetchChartData(chartType, period = 'month') {
    console.log(`[DashboardAPI] Fetching chart data: ${chartType} for ${period}`);
    
    try {
      const response = await this.api.get(`/dashboard/${chartType}`, {
        params: { period }
      });
      console.log(`[DashboardAPI] Chart data fetched successfully: ${chartType}`);
      return response.data;
    } catch (error) {
      console.error(`[DashboardAPI] Error fetching chart data ${chartType}:`, error);
      throw error;
    }
  }

  /**
   * Handle data changes and invalidate relevant caches
   */
  handleDataChange(changeType, entityId) {
    console.log(`[DashboardAPI] Handling data change: ${changeType}`);
    
    switch (changeType) {
      case 'order_created':
      case 'order_updated':
      case 'order_deleted':
        this.cache.invalidate('order_stats');
        this.cache.invalidate('quick_stats');
        this.cache.invalidate('dashboard_stats');
        break;
        
      case 'payment_created':
      case 'payment_updated':
      case 'payment_deleted':
        this.cache.invalidate('payment_stats');
        this.cache.invalidate('quick_stats');
        this.cache.invalidate('dashboard_stats');
        break;
        
      case 'expense_created':
      case 'expense_updated':
      case 'expense_deleted':
        this.cache.invalidate('quick_stats');
        this.cache.invalidate('dashboard_stats');
        break;
        
      default:
        // For unknown changes, invalidate dashboard stats to be safe
        this.cache.invalidate('dashboard_stats');
        this.cache.invalidate('quick_stats');
        break;
    }
  }

  /**
   * Force refresh specific data type
   */
  async forceRefresh(dataType) {
    console.log(`[DashboardAPI] Force refreshing ${dataType}`);
    
    // Invalidate cache first
    this.cache.invalidate(dataType);
    
    // Then fetch fresh data
    switch (dataType) {
      case 'dashboard_stats':
        return await this.getDashboardStats();
      case 'payment_stats':
        return await this.getPaymentStats();
      case 'order_stats':
        return await this.getOrderStats();
      case 'quick_stats':
        return await this.getQuickStats();
      default:
        console.warn(`[DashboardAPI] Unknown data type for refresh: ${dataType}`);
        return null;
    }
  }

  /**
   * Get real-time data without caching (for critical updates)
   */
  async getRealTimeData(dataType) {
    switch (dataType) {
      case 'dashboard_stats':
        return await this.fetchDashboardStats();
      case 'payment_stats':
        return await this.fetchPaymentStats();
      case 'order_stats':
        return await this.fetchOrderStats();
      case 'quick_stats':
        return await this.fetchQuickStats();
      default:
        return null;
    }
  }

  /**
   * Preload critical dashboard data (only if user has permissions)
   */
  async preloadDashboardData() {
    console.log('[DashboardAPI] Checking permissions before preloading dashboard data');
    
    try {
      // Check if user has dashboard permissions before attempting to preload
      // Use authStore directly to avoid Vue inject() warning when called outside setup context
      const authStore = useAuthStore();
      
      if (!authStore.hasPermission('dashboard.view_stats')) {
        console.log('[DashboardAPI] User does not have dashboard.view_stats permission, skipping preload');
        return;
      }
      
      console.log('[DashboardAPI] User has dashboard permissions, preloading critical dashboard data');
      
      // Preload most important data in parallel
      const results = await Promise.allSettled([
        this.getDashboardStats(),
        this.getQuickStats(),
        this.getOrderStats(),
        this.getPaymentStats()
      ]);
      
      // Log any failed preloads but don't throw errors
      results.forEach((result, index) => {
        const dataTypes = ['dashboard_stats', 'quick_stats', 'order_stats', 'payment_stats'];
        if (result.status === 'rejected') {
          console.warn(`[DashboardAPI] Failed to preload ${dataTypes[index]}:`, result.reason);
        }
      });
      
      console.log('[DashboardAPI] Dashboard data preload completed');
    } catch (error) {
      console.warn('[DashboardAPI] Error during dashboard data preload (non-critical):', error);
      // Don't throw error - this should not fail app initialization
    }
  }
}

// Create singleton instance
const dashboardApi = new DashboardApiService();

// Export composable
export function useDashboardApi() {
  return {
    // Data fetching methods
    getDashboardStats: (filters) => dashboardApi.getDashboardStats(filters),
    getPaymentStats: (dateRange) => dashboardApi.getPaymentStats(dateRange),
    getOrderStats: (dateRange) => dashboardApi.getOrderStats(dateRange),
    getQuickStats: () => dashboardApi.getQuickStats(),
    getChartData: (chartType, period) => dashboardApi.getChartData(chartType, period),
    
    // Cache management
    handleDataChange: (changeType, entityId) => dashboardApi.handleDataChange(changeType, entityId),
    forceRefresh: (dataType) => dashboardApi.forceRefresh(dataType),
    preloadDashboardData: () => dashboardApi.preloadDashboardData(),
    getRealTimeData: (dataType) => dashboardApi.getRealTimeData(dataType),
    
    // Utilities
    getCacheStats: () => dashboardApi.getCacheStats(),
  };
}

export default dashboardApi; 